import React from 'react'
import { Table, Tag, Button } from 'antd'
import { EyeOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { Order } from '../types/order.types'
import { formatINR } from '../../../shared/utils/formatters'

interface OrdersTableProps {
    orders: Order[]
    totalOrders: number
    currentPage: number
    pageSize: number
    onPageChange: (page: number, pageSize: number) => void
    onRowClick: (order: Order) => void
    selectedRowKeys?: React.Key[]
    onSelectionChange?: (selectedRowKeys: React.Key[]) => void
}

export const OrdersTable: React.FC<OrdersTableProps> = ({
    orders,
    totalOrders,
    currentPage,
    pageSize,
    onPageChange,
    onRowClick,
    selectedRowKeys,
    onSelectionChange
}) => {
    // ... (color helpers kept same)
    const getStatusColor = (status: string) => {
        const colors = {
            confirmed: 'blue',
            packed: 'orange',
            shipped: 'cyan',
            delivered: 'green',
            cancelled: 'red',
        }
        return colors[status as keyof typeof colors] || 'default'
    }

    const getPaymentStatusColor = (status: string) => {
        const colors = {
            pending: 'orange',
            paid: 'green',
            refunded: 'gray',
        }
        return colors[status as keyof typeof colors] || 'default'
    }

    const columns: ColumnsType<Order> = [
        {
            title: 'Order ID',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => <strong>{id}</strong>,
        },
        // ... (other columns kept same - omitting to save tokens, I will use original content references if possible but replace_file needs content)
        // I will copy columns from original file content provided in previous turn to ensure no data loss.
        // Actually, I can use "chunk replacement" for props and pagination separately if "columns" is too big.
        // But let's look at file size, it's small enough.
        {
            title: 'RFQ ID',
            dataIndex: 'rfqId',
            key: 'rfqId',
        },
        {
            title: 'Customer',
            dataIndex: 'customer_name',
            key: 'customer_name',
        },
        {
            title: 'Merchant',
            dataIndex: 'merchant_name',
            key: 'merchant_name',
        },
        {
            title: 'Product',
            dataIndex: 'items',
            key: 'items',
            render: (items: any[]) => items?.[0]?.product_id || 'Product',
        },
        {
            title: 'Amount',
            dataIndex: 'total_amount',
            key: 'total_amount',
            render: (amount: number | string) => formatINR(amount),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
            ),
        },
        {
            title: 'Payment',
            dataIndex: 'payment_status',
            key: 'payment_status',
            render: (status: string) => (
                <Tag color={getPaymentStatusColor(status || 'pending')}>{status?.toUpperCase() || 'PENDING'}</Tag>
            ),
        },
        {
            title: 'Date',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date: string) => new Date(date).toLocaleDateString()
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => onRowClick(record)}>
                    View
                </Button>
            ),
        },
    ]

    return (
        <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            onRow={(record) => ({
                onClick: () => onRowClick(record),
                style: { cursor: 'pointer' },
            })}
            rowSelection={
                onSelectionChange
                    ? {
                        selectedRowKeys,
                        onChange: onSelectionChange,
                    }
                    : undefined
            }
            pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalOrders,
                showSizeChanger: true,
                onChange: onPageChange,
                showTotal: (total) => `Total ${total} orders`,
            }}
        />
    )
}
